/**
 * -----------------------------------------------------------------------------
 * Popup - Create New Dashboard
 * -----------------------------------------------------------------------------
 * This popup is used for creating a new dashboard for the user.
 */

import { Formik, useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { serializeErrorKeyValues } from "../../api/network/errorCodes";
import {
  useCreateDashboardMutation,
  useUpdateDashboardMutation,
} from "../../api/network/overviewApiServices";
import {
  setModalsData,
  TListingQueryParams,
  TModalsState,
} from "../../api/store/commonSlice";
import {
  AdminFormFieldInput,
  AdminFormFieldSubmit,
} from "../../components/admin/formFields";
import FTModal from "../../components/common/FTModal";
import {
  formInitialValues,
  TFormFieldNames,
  YupValidationSchema,
} from "./validation";
import { useOverviewContext } from "./context/overview.context";
import FormActionButtons from "../../components/admin/formActionButtons";
import { useMemo } from "react";

const ModalCreateNewDashboard = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "overviewScreen.create_new",
  });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const dispatch = useDispatch();

  const { refetchDashboardList, dashList, setSelectedDashboard } =
    useOverviewContext();

  // dashboard mutation
  const [createDashboardAPITrigger, { isLoading: isLoadingCreateDashboard }] =
    useCreateDashboardMutation();
  const [updateDashboardAPITrigger, { isLoading: isLoadingUpdateDashboard }] =
    useUpdateDashboardMutation();

  // const hideModal = (selectNewDashboard: number | void) => {
  //   dispatch(setModalsData({
  //     ...modalsState,
  //     showCreateDashboard: false,
  //     editDashboardId: null,
  //   }));
  //   if (!!selectNewDashboard) {
  //     setSelectedDashboard(selectNewDashboard);
  //   }
  // };
  const hideModal = (selectNewDashboard?: number) => {
    dispatch(
      setModalsData({
        ...modalsState,
        showCreateDashboard: false,
        editDashboardId: null,
      })
    );

    if (typeof selectNewDashboard === "number") {
      setSelectedDashboard(selectNewDashboard);
    }
  };

  const formik = useFormik({
    initialValues: {
      ...formInitialValues,
      dashboard_name: !!modalsState?.editDashboardId
        ? dashList?.find((d) => d.id === modalsState?.editDashboardId)?.name
        : formInitialValues.dashboard_name,
    },
    validationSchema: YupValidationSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      (!!modalsState?.editDashboardId
        ? updateDashboardAPITrigger({
            id: modalsState?.editDashboardId,
            name: values.dashboard_name,
          })
        : createDashboardAPITrigger({
            name: values.dashboard_name,
          })
      )
        .unwrap()
        .then((data) => {
          if (!!data?.id) {
            toast.success(
              t(
                !!modalsState.editDashboardId
                  ? "toast.update_success"
                  : "toast.create_success"
              )
            );
            hideModal(data?.id);
            refetchDashboardList();
          } else {
            toast.error(
              t(
                !!modalsState.editDashboardId
                  ? "toast.update_failed"
                  : "toast.create_failed"
              )
            );
          }
          resetForm();
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [
                t(
                  !!modalsState.editDashboardId
                    ? "toast.update_failed"
                    : "toast.create_failed"
                ),
              ];
          toast.error(errors?.join(" "));
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    dirty,
    setFieldValue,
  } = formik;
  const invalidFields = Object.keys(errors).filter(
    (key) => !!errors[key as TFormFieldNames]
  );
  const isFormValid = invalidFields.length === 0 && dirty;

  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              label: t(
                !!modalsState?.editDashboardId
                  ? "update_button"
                  : "submit_button"
              ),
              variant: "primary",
              disabled:
                !isFormValid ||
                isSubmitting ||
                isLoadingCreateDashboard ||
                isLoadingUpdateDashboard,
            },
            {
              label: tMain("cancel"),
              type: "button",
              variant: "danger-transparent",
              disabled:
                isSubmitting ||
                isLoadingCreateDashboard ||
                isLoadingUpdateDashboard,
              onClick: () => hideModal(),
            },
          ]}
        />
      </>
    );
  }, [
    isFormValid,
    isSubmitting,
    isLoadingCreateDashboard,
    isLoadingUpdateDashboard,
  ]);

  return (
    <FTModal
      visible={modalsState.showCreateDashboard}
      heading={t(!!modalsState?.editDashboardId ? "heading_edit" : "heading")}
      onHide={hideModal}
    >
      <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
        <AdminFormFieldInput
          label={t("dashboard_name")}
          type="text"
          id="DashboardName"
          name="dashboard_name"
          placeholder={t("dashboard_name_placeholder")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.dashboard_name}
          touched={touched.dashboard_name}
          error={errors.dashboard_name}
        />

        {MemoizedFormActionButtons}

        {/* <AdminFormFieldSubmit
          label={t(
            !!modalsState?.editDashboardId ? "update_button" : "submit_button"
          )}
          variant="primary"
          disabled={
            !isFormValid ||
            isSubmitting ||
            isLoadingCreateDashboard ||
            isLoadingUpdateDashboard
          }
        />

        <AdminFormFieldSubmit
          label={tMain("cancel")}
          type="button"
          variant="danger-transparent"
          disabled={
            isSubmitting || isLoadingCreateDashboard || isLoadingUpdateDashboard
          }
          onClick={() => hideModal()}
        /> */}
      </form>
    </FTModal>
  );
};
export default ModalCreateNewDashboard;
